import { Button } from './ui/Button';
import { motion, useScroll, useTransform, useSpring, useReducedMotion } from 'motion/react';
import { Sparkles, Zap, Trophy, ArrowRight, ShieldCheck, Gauge, Code2 } from 'lucide-react';
import React, { useMemo, useRef } from 'react';

function Particle({ scrollYProgress, left, top, factor, duration, delay, size, blur }) {
    const particleY = useTransform(scrollYProgress, [0, 1], [0, -120 * factor]);

    return (
        <motion.div
            className="absolute rounded-full"
            style={{
                left,
                top,
                y: particleY,
                width: size,
                height: size,
                filter: `blur(${blur}px)`,
            }}
            animate={{
                y: [0, -26, 0],
                opacity: [0, 0.9, 0],
                scale: [1, 1.15, 1],
            }}
            transition={{
                duration,
                repeat: Infinity,
                delay,
            }}
        >
            <div className="w-full h-full bg-primary/20 rounded-full" />
        </motion.div>
    );
}

export function Hero() {
    const reduceMotion = useReducedMotion();
    const ref = useRef(null);

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start'],
    });

    const yRaw = useTransform(scrollYProgress, [0, 1], ['0%', '55%']);
    const innerYRaw = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
    const scaleRaw = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
    const opacityRaw = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.92, 0]);

    const y = useSpring(yRaw, { stiffness: 90, damping: 22 });
    const innerY = useSpring(innerYRaw, { stiffness: 90, damping: 22 });
    const scale = useSpring(scaleRaw, { stiffness: 90, damping: 22 });
    const opacity = useSpring(opacityRaw, { stiffness: 90, damping: 22 });

    const scrollToContact = () => {
        document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
    };

    const highlights = [
        { icon: Zap, text: 'Schnelle Ladezeiten' },
        { icon: Sparkles, text: 'Modernes Design' },
        { icon: Trophy, text: 'SEO-Optimiert' },
    ];

    // Statt “Badges-Aufzählung”: 3 Feature-Cards + ein Value-Bar
    const featureCards = useMemo(
        () => [
            {
                icon: Gauge,
                title: 'Performance',
                desc: 'Core Web Vitals & schnelle Ladezeiten – damit Nutzer bleiben und Google dich liebt.',
            },
            {
                icon: ShieldCheck,
                title: 'DSGVO & Sicherheit',
                desc: 'Saubere Umsetzung mit Best Practices, Updates & Basics, die wirklich zählen.',
            },
            {
                icon: Code2,
                title: 'Sauberer Code',
                desc: 'Wartbar, skalierbar, klar strukturiert – damit dein Projekt langfristig funktioniert.',
            },
        ],
        []
    );

    // KPIs
    const stats = [
        { value: '100%', label: 'Kundenzufriedenheit', icon: Trophy },
        { value: '6+ Jahre', label: 'Erfahrung', icon: Zap },
    ];

    const particles = useMemo(() => {
        const count = 24;
        return Array.from({ length: count }, (_, i) => {
            const base = Math.random();
            return {
                id: i,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                factor: (i % 4) + 1,
                duration: 2.8 + Math.random() * 2.8,
                delay: Math.random() * 2.5,
                size: 4 + Math.floor(Math.random() * 8),
                blur: Math.random() > 0.6 ? 1.5 : 0,
                glow: base > 0.82,
            };
        });
    }, []);

    return (
        <section ref={ref} className="relative min-h-screen overflow-hidden px-4 pt-28 pb-20">
            {/* Background */}
            <motion.div className="absolute inset-0" style={{ y }} aria-hidden="true">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50" />

                {/* Aurora blobs */}
                <motion.div
                    className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
                    style={{
                        background: 'radial-gradient(circle, rgba(59,130,246,0.35), transparent 60%)',
                        y: innerY,
                    }}
                    animate={
                        reduceMotion
                            ? undefined
                            : { scale: [1, 1.08, 1], x: [0, 22, 0], opacity: [0.32, 0.5, 0.32] }
                    }
                    transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                    className="absolute -bottom-32 -right-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-40"
                    style={{
                        background: 'radial-gradient(circle, rgba(168,85,247,0.35), transparent 60%)',
                        y: innerY,
                    }}
                    animate={
                        reduceMotion
                            ? undefined
                            : { scale: [1, 1.1, 1], x: [0, -26, 0], opacity: [0.28, 0.46, 0.28] }
                    }
                    transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Subtle grid */}
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{
                        backgroundImage:
                            'linear-gradient(rgba(0,0,0,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.18) 1px, transparent 1px)',
                        backgroundSize: '56px 56px',
                        maskImage: 'radial-gradient(circle at 40% 30%, black, transparent 65%)',
                    }}
                />
            </motion.div>

            {/* Particles */}
            <div className="absolute inset-0" aria-hidden="true">
                {particles.map((p) => (
                    <React.Fragment key={p.id}>
                        <Particle
                            scrollYProgress={scrollYProgress}
                            left={p.left}
                            top={p.top}
                            factor={p.factor}
                            duration={p.duration}
                            delay={p.delay}
                            size={p.size}
                            blur={p.blur}
                        />
                        {p.glow && (
                            <motion.div
                                className="absolute rounded-full"
                                style={{
                                    left: p.left,
                                    top: p.top,
                                    width: p.size * 5,
                                    height: p.size * 5,
                                    background: 'radial-gradient(circle, rgba(147,51,234,0.18), transparent 60%)',
                                    filter: 'blur(10px)',
                                }}
                                animate={reduceMotion ? undefined : { opacity: [0.15, 0.35, 0.15] }}
                                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: p.delay }}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Content */}
            <motion.div className="relative z-10 max-w-5xl mx-auto" style={{ opacity, scale }}>
                <div className="text-center">
                    {/* Badge */}
                    <motion.div
                        className="mx-auto inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 backdrop-blur px-4 py-2 shadow-sm"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.6 }}
                    >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white">
              ✨
            </span>
                        <span className="text-sm text-gray-700">Ihre digitale Zukunft beginnt hier</span>
                        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              <Sparkles className="h-3.5 w-3.5" />
              Premium Web
            </span>
                    </motion.div>

                    {/* Headline */}
                    <motion.h1
                        className="mt-6 text-5xl md:text-7xl leading-[1.03] tracking-tight"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25, duration: 0.8 }}
                    >
            <span className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 bg-clip-text text-transparent">
              Webseiten, die Ihr Business
            </span>
                        <span className="block mt-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              aufs nächste Level bringen
            </span>
                    </motion.h1>

                    {/* Subtext */}
                    <motion.p
                        className="mt-6 text-xl text-gray-600 leading-relaxed mx-auto max-w-3xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.45, duration: 0.8 }}
                    >
                        Von der Vision zur digitalen Realität – maßgeschneiderte Web-Lösungen, die Vertrauen schaffen,
                        schnell laden und messbar mehr Anfragen bringen.
                    </motion.p>

                    {/* CTA */}
                    <motion.div
                        className="mt-10 flex flex-wrap justify-center gap-4"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.6 }}
                    >
                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                size="lg"
                                onClick={scrollToContact}
                                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg text-white"
                            >
                <span className="relative z-10 flex items-center text-white">
                  Kostenloses Erstgespräch
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform text-white" />
                </span>
                                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.35),transparent_60%)]" />
                            </Button>
                        </motion.div>

                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                size="lg"
                                variant="outline"
                                className="border-2 bg-white/70 backdrop-blur hover:bg-white"
                                onClick={() => document.getElementById('projekte')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                Projekte ansehen
                            </Button>
                        </motion.div>
                    </motion.div>

                    {/* Highlights row (bleibt, aber clean) */}
                    <motion.div
                        className="mt-8 flex flex-wrap justify-center gap-3"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.6 }}
                    >
                        {highlights.map((h, i) => {
                            const Icon = h.icon;
                            return (
                                <motion.div
                                    key={i}
                                    className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 backdrop-blur px-4 py-2 shadow-sm"
                                    whileHover={{ y: -2 }}
                                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-600/15 to-purple-600/15">
                    <Icon className="h-4 w-4 text-purple-700" />
                  </span>
                                    <span className="text-sm text-gray-800">{h.text}</span>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>

                {/* NEW: Feature cards instead of “badge list” */}
                <motion.div
                    className="mt-12 grid md:grid-cols-3 gap-4"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.6 }}
                >
                    {featureCards.map((f) => {
                        const Icon = f.icon;
                        return (
                            <motion.div
                                key={f.title}
                                className="group relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg p-5 text-left"
                                whileHover={{ y: -6 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                            >
                                <div className="pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full bg-gradient-to-br from-blue-600/15 to-purple-600/15 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-md">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="text-base font-semibold text-gray-900">{f.title}</div>
                                        <div className="mt-1 text-sm text-gray-600 leading-relaxed">{f.desc}</div>
                                    </div>
                                </div>

                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* KPIs */}
                <motion.div
                    className="mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-4 pt-8 border-t border-gray-200/70"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.15, duration: 0.8 }}
                >
                    {stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={stat.label}
                                className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-md px-6 py-5 text-left min-w-[240px]"
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.25 + index * 0.1, duration: 0.45 }}
                                whileHover={{ y: -3, scale: 1.01 }}
                            >
                                <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-gradient-to-br from-blue-600/15 to-purple-600/15 blur-2xl" />
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-md">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="text-2xl md:text-3xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                            {stat.value}
                                        </div>
                                        <div className="text-sm text-gray-600">{stat.label}</div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </motion.div>

            {/* Bottom fade */}
            <div
                className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/85 to-transparent"
                aria-hidden="true"
            />
        </section>
    );
}
